import { supabase } from '../lib/supabase'

export interface UpstreamNode {
  siteId: string
  /** Link row di mana `siteId` berperan sebagai NE menuju parent-nya. */
  link: any
  depth: number
  children: UpstreamNode[]
}

/** Batas kedalaman penelusuran ke hulu agar query tetap terkendali. */
const MAX_DEPTH = 8
/** Batas total node supaya hub besar tidak membanjiri drawer. */
const MAX_NODES = 400

/**
 * Menelusuri seluruh rantai di hulu sebuah site: semua NE yang mengarah
 * ke site tersebut, lalu NE yang mengarah ke NE itu, dan seterusnya
 * sampai tidak ada lagi link masuk (ujung radio).
 *
 * Penelusuran dilakukan per level dengan satu query `.in()` sehingga
 * jumlah round-trip tetap kecil meski cabangnya banyak.
 */
export async function fetchUpstreamTree(
  regional: string,
  rootSiteId: string,
): Promise<{ children: UpstreamNode[]; rows: any[]; truncated: boolean }> {
  const rows: any[] = []
  const visited = new Set<string>([rootSiteId])
  const nodesByParent = new Map<string, UpstreamNode[]>()

  let frontier = [rootSiteId]
  let truncated = false
  let total = 0

  for (let depth = 1; depth <= MAX_DEPTH && frontier.length; depth += 1) {
    const { data: incoming, error } = await supabase
      .from('link_routes')
      .select('*')
      .eq('regional', regional)
      .in('site_id_fe', frontier)
      .limit(500)

    if (error || !incoming?.length) break

    const nextFrontier: string[] = []

    incoming.forEach((row) => {
      const childId = row.site_id_ne
      if (!childId || visited.has(childId)) return
      if (total >= MAX_NODES) {
        truncated = true
        return
      }

      visited.add(childId)
      total += 1
      rows.push(row)

      const node: UpstreamNode = {
        siteId: childId,
        link: row,
        depth,
        children: [],
      }

      const siblings = nodesByParent.get(row.site_id_fe) || []
      siblings.push(node)
      nodesByParent.set(row.site_id_fe, siblings)

      nextFrontier.push(childId)
    })

    frontier = nextFrontier
  }

  // Rakit pohon dari peta parent ➝ children.
  const attach = (node: UpstreamNode) => {
    node.children = nodesByParent.get(node.siteId) || []
    node.children.forEach(attach)
  }

  const roots = nodesByParent.get(rootSiteId) || []
  roots.forEach(attach)

  return { children: roots, rows, truncated }
}

/** Menghitung total node pada sebuah pohon upstream. */
export function countUpstream(nodes: UpstreamNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + countUpstream(node.children), 0)
}

/** Node tanpa anak = ujung rantai radio. */
export function countLeaves(nodes: UpstreamNode[]): number {
  return nodes.reduce(
    (sum, node) =>
      sum + (node.children.length ? countLeaves(node.children) : 1),
    0,
  )
}

/** Kedalaman maksimum rantai. */
export function maxDepth(nodes: UpstreamNode[]): number {
  return nodes.reduce(
    (deepest, node) => Math.max(deepest, node.depth, maxDepth(node.children)),
    0,
  )
}
